import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    BookOpen, Play, Clock, CheckCircle, ChevronDown, ChevronRight,
    ArrowLeft, Search, Filter, StickyNote, Star, X, Loader2,
    ChevronUp, ChevronDown as ChevronDownIcon
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import Button from '../components/Button'
import ModalParabens from '../components/ModalParabens'
import MaterialList from '../components/MaterialList'
import MaterialUploadAlternativo from '../components/MaterialUploadAlternativo'
import { useToast } from '../hooks/useToast'
import { ToastContainer } from '../components/Toast'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
}

interface Modulo {
    id: string
    titulo: string
    descricao: string | null
    ordem_exibicao: number
    aulas: Aula[]
    progresso: number
    total_aulas: number
    aulas_assistidas: number
}

interface Aula {
    id: string
    titulo: string
    descricao: string | null
    video_url: string | null
    duracao: number | null
    ordem_exibicao: number
    status: string
    assistida: boolean
    data_assistida: string | null
    modulo_id: string
    notas?: string
    favorita?: boolean
}

interface FiltroAulas {
    status: 'todas' | 'assistidas' | 'pendentes' | 'favoritas'
    busca: string
}

export default function CursoDetalhes() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { showSuccess, showError, toasts } = useToast()
    const [curso, setCurso] = useState<Curso | null>(null)
    const [modulos, setModulos] = useState<Modulo[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [modulosExpandidos, setModulosExpandidos] = useState<Set<string>>(new Set())
    const [showModalParabens, setShowModalParabens] = useState(false)
    const [progressoAnterior, setProgressoAnterior] = useState(0)
    const [currentVideo, setCurrentVideo] = useState<{ url: string; title: string; aulaId?: string } | null>(null)
    const [filtros, setFiltros] = useState<FiltroAulas>({ status: 'todas', busca: '' })
    const [showFiltros, setShowFiltros] = useState(false)
    const [showNotasModal, setShowNotasModal] = useState(false)
    const [currentAulaNotas, setCurrentAulaNotas] = useState<{ id: string; titulo: string; notas: string } | null>(null)
    const [loadingFavoritas, setLoadingFavoritas] = useState<Set<string>>(new Set())
    const [showMaterialUpload, setShowMaterialUpload] = useState(false)
    const [loadingNotas, setLoadingNotas] = useState<Set<string>>(new Set())
    const [loadingAssistir, setLoadingAssistir] = useState<Set<string>>(new Set())

    // Usar ref para controlar se já carregou uma vez, preservando entre rerenders
    const dadosJaCarregados = useRef(false)

    useEffect(() => {
        if (id && user && !dadosJaCarregados.current) {
            loadCursoDetalhes()
            dadosJaCarregados.current = true
        }
    }, [id, user])

    // Preservar o estado do vídeo e curso ao mudar de aba
    useEffect(() => {
        const handleVisibilityChange = () => {
            // Quando a aba volta a ficar visível, não fazer nada especial
            // O estado React já preserva os dados
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    // Carregar materiais de apoio quando a aula atual mudar

    // Detectar quando o progresso chega a 100%
    useEffect(() => {
        const progressoTotal = calcularProgressoTotal()

        // Se o progresso chegou a 100% e não estava antes, mostrar modal
        if (progressoTotal >= 100 && progressoAnterior < 100 && !loading) {
            setShowModalParabens(true)
        }

        setProgressoAnterior(progressoTotal)
    }, [modulos, loading])

    // Detectar mudanças no progresso após atualizações otimistas
    useEffect(() => {
        if (!loading && modulos.length > 0) {
            const progressoTotal = calcularProgressoTotal()

            // Se o progresso chegou a 100% e não estava antes, mostrar modal
            if (progressoTotal >= 100 && progressoAnterior < 100) {
                setShowModalParabens(true)
            }

            setProgressoAnterior(progressoTotal)
        }
    }, [modulos])

    const loadCursoDetalhes = async () => {
        try {
            setLoading(true)
            setError('')

            // Carregar dados do curso
            const { data: cursoData, error: cursoError } = await supabase
                .from('cursos')
                .select('*')
                .eq('id', id)
                .single()

            if (cursoError) throw cursoError
            setCurso(cursoData)

            // Carregar módulos do curso
            const { data: modulosData, error: modulosError } = await supabase
                .from('modulos')
                .select('*')
                .eq('curso_id', id)
                .order('ordem_exibicao')

            if (modulosError) throw modulosError

            // Para cada módulo, carregar aulas e progresso
            const modulosComAulas = await Promise.all(
                (modulosData || []).map(async (modulo) => {
                    // Carregar aulas do módulo
                    const { data: aulasData, error: aulasError } = await supabase
                        .from('aulas')
                        .select(`
                            *,
                            progresso_aulas!left(
                                assistida,
                                data_assistida,
                                notas,
                                favorita
                            )
                        `)
                        .eq('modulo_id', modulo.id)
                        .eq('status', 'ativo')
                        .order('ordem_exibicao')

                    if (aulasError) throw aulasError

                    // Processar aulas com status de assistida
                    const aulas = (aulasData || []).map(aula => ({
                        ...aula,
                        assistida: aula.progresso_aulas?.[0]?.assistida || false,
                        data_assistida: aula.progresso_aulas?.[0]?.data_assistida || null,
                        modulo_id: modulo.id,
                        notas: aula.progresso_aulas?.[0]?.notas || '',
                        favorita: aula.progresso_aulas?.[0]?.favorita || false
                    }))

                    // Calcular progresso do módulo
                    const totalAulas = aulas.length
                    const aulasAssistidas = aulas.filter(aula => aula.assistida).length
                    const progresso = totalAulas > 0 ? (aulasAssistidas / totalAulas) * 100 : 0

                    return {
                        ...modulo,
                        aulas,
                        progresso: Math.round(progresso),
                        total_aulas: totalAulas,
                        aulas_assistidas: aulasAssistidas
                    }
                })
            )

            setModulos(modulosComAulas)

            // Calcular duração total do curso somando todas as aulas
            const duracaoTotal = modulosComAulas.reduce((total: number, modulo) => {
                const duracaoModulo = modulo.aulas.reduce((modTotal: number, aula: { duracao?: number }) => {
                    return modTotal + (aula.duracao || 0)
                }, 0)
                return total + duracaoModulo
            }, 0)

            // Atualizar curso com duração calculada
            if (cursoData) {
                setCurso({
                    ...cursoData,
                    duracao_estimada: duracaoTotal
                })
            }

            // Expandir primeiro módulo por padrão
            if (modulosComAulas.length > 0) {
                setModulosExpandidos(new Set([modulosComAulas[0].id]))

                // Selecionar automaticamente a primeira aula se houver vídeo
                const primeiraAula = modulosComAulas[0].aulas.find((aula: Aula) => aula.video_url)
                if (primeiraAula && !currentVideo) {
                    setCurrentVideo({
                        url: primeiraAula.video_url!,
                        title: primeiraAula.titulo,
                        aulaId: primeiraAula.id
                    })
                }
            }

        } catch (err) {
            console.error('Erro ao carregar detalhes do curso:', err)
            setError('Erro ao carregar detalhes do curso. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const marcarAulaAssistida = async (aulaId: string) => {
        if (!user) return

        // Marcar como loading
        setLoadingAssistir(prev => new Set(prev).add(aulaId))

        // Atualização otimista - atualizar o estado local imediatamente
        const atualizarEstadoLocal = () => {
            setModulos(prevModulos => {
                const modulosAtualizados = prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? {
                                ...aula,
                                assistida: true,
                                data_assistida: new Date().toISOString()
                            }
                            : aula
                    )
                }))

                // Recalcular progresso dos módulos
                return recalcularProgressoModulos(modulosAtualizados)
            })
        }

        // Atualizar estado local primeiro
        atualizarEstadoLocal()

        try {
            // Verificar se já existe um registro de progresso para esta aula
            const { data: existingProgress, error: checkError } = await supabase
                .from('progresso_aulas')
                .select('*')
                .eq('user_id', user.id)
                .eq('aula_id', aulaId)
                .single()

            if (checkError && checkError.code !== 'PGRST116') {
                throw checkError
            }

            if (existingProgress) {
                // Atualizar registro existente
                const { error: updateError } = await supabase
                    .from('progresso_aulas')
                    .update({
                        assistida: true,
                        data_assistida: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', user.id)
                    .eq('aula_id', aulaId)

                if (updateError) throw updateError
            } else {
                // Criar novo registro
                const { error: insertError } = await supabase
                    .from('progresso_aulas')
                    .insert({
                        user_id: user.id,
                        aula_id: aulaId,
                        assistida: true,
                        data_assistida: new Date().toISOString(),
                        notas: '',
                        favorita: false,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })

                if (insertError) throw insertError
            }

            // Mostrar mensagem de sucesso
            showSuccess('Aula marcada como assistida!')

        } catch (err) {
            console.error('Erro ao marcar aula como assistida:', err)
            showError('Erro ao marcar aula como assistida. Tente novamente.')

            // Reverter a mudança local em caso de erro
            setModulos(prevModulos => {
                const modulosRevertidos = prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? {
                                ...aula,
                                assistida: false,
                                data_assistida: null
                            }
                            : aula
                    )
                }))

                // Recalcular progresso após reversão
                return recalcularProgressoModulos(modulosRevertidos)
            })
        } finally {
            // Remover loading
            setLoadingAssistir(prev => {
                const newSet = new Set(prev)
                newSet.delete(aulaId)
                return newSet
            })
        }
    }

    const removerAulaAssistida = async (aulaId: string) => {
        if (!user) return

        // Marcar como loading
        setLoadingAssistir(prev => new Set(prev).add(aulaId))

        // Atualização otimista - atualizar o estado local imediatamente
        const atualizarEstadoLocal = () => {
            setModulos(prevModulos => {
                const modulosAtualizados = prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? {
                                ...aula,
                                assistida: false,
                                data_assistida: null
                            }
                            : aula
                    )
                }))

                // Recalcular progresso dos módulos
                return recalcularProgressoModulos(modulosAtualizados)
            })
        }

        // Atualizar estado local primeiro
        atualizarEstadoLocal()

        try {
            // Verificar se existe um registro de progresso para esta aula
            const { data: existingProgress, error: checkError } = await supabase
                .from('progresso_aulas')
                .select('*')
                .eq('user_id', user.id)
                .eq('aula_id', aulaId)
                .single()

            if (checkError && checkError.code !== 'PGRST116') {
                throw checkError
            }

            if (existingProgress) {
                // Atualizar registro existente
                const { error: updateError } = await supabase
                    .from('progresso_aulas')
                    .update({
                        assistida: false,
                        data_assistida: null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', user.id)
                    .eq('aula_id', aulaId)

                if (updateError) throw updateError
            } else {
                // Criar novo registro com assistida = false
                const { error: insertError } = await supabase
                    .from('progresso_aulas')
                    .insert({
                        user_id: user.id,
                        aula_id: aulaId,
                        assistida: false,
                        data_assistida: null,
                        notas: '',
                        favorita: false,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })

                if (insertError) throw insertError
            }

            // Mostrar mensagem de sucesso
            showSuccess('Status de assistida removido!')

        } catch (err) {
            console.error('Erro ao remover status de assistida:', err)
            showError('Erro ao remover status de assistida. Tente novamente.')

            // Reverter a mudança local em caso de erro
            setModulos(prevModulos => {
                const modulosRevertidos = prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? {
                                ...aula,
                                assistida: true,
                                data_assistida: new Date().toISOString()
                            }
                            : aula
                    )
                }))

                // Recalcular progresso após reversão
                return recalcularProgressoModulos(modulosRevertidos)
            })
        } finally {
            // Remover loading
            setLoadingAssistir(prev => {
                const newSet = new Set(prev)
                newSet.delete(aulaId)
                return newSet
            })
        }
    }

    const openVideoPlayer = (videoUrl: string, videoTitle: string, aulaId?: string) => {
        setCurrentVideo({ url: videoUrl, title: videoTitle, aulaId })
        // Não precisamos mais do modal, o player está integrado na página
    }

    // Função para lidar com upload de materiais
    const handleMaterialUploadSuccess = (_material: any) => {
        showSuccess('Material adicionado com sucesso!')
        // Aqui você pode atualizar a lista de materiais se necessário
    }

    const handleMaterialUploadError = (error: string) => {
        showError(`Erro ao adicionar material: ${error}`)
    }


    const salvarNotasAula = async (aulaId: string, notas: string) => {
        if (!user) return

        // Marcar como loading
        setLoadingNotas(prev => new Set(prev).add(aulaId))

        // Atualização otimista - atualizar o estado local imediatamente
        const atualizarEstadoLocal = (novasNotas: string) => {
            setModulos(prevModulos =>
                prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? { ...aula, notas: novasNotas }
                            : aula
                    )
                }))
            )
        }

        // Salvar estado anterior para possível reversão
        const estadoAnterior = modulos
            .flatMap(m => m.aulas)
            .find(aula => aula.id === aulaId)?.notas || ''

        // Atualizar estado local primeiro
        atualizarEstadoLocal(notas)

        try {
            // Verificar se já existe um registro de progresso para esta aula
            const { data: existingProgress, error: checkError } = await supabase
                .from('progresso_aulas')
                .select('*')
                .eq('user_id', user.id)
                .eq('aula_id', aulaId)
                .single()

            if (checkError && checkError.code !== 'PGRST116') {
                throw checkError
            }

            if (existingProgress) {
                // Atualizar notas no registro existente
                const { error: updateError } = await supabase
                    .from('progresso_aulas')
                    .update({
                        notas: notas,
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', user.id)
                    .eq('aula_id', aulaId)

                if (updateError) throw updateError
            } else {
                // Criar novo registro com as notas
                const { error: insertError } = await supabase
                    .from('progresso_aulas')
                    .insert({
                        user_id: user.id,
                        aula_id: aulaId,
                        assistida: false,
                        data_assistida: null,
                        notas: notas,
                        favorita: false,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })

                if (insertError) throw insertError
            }

            // Mostrar mensagem de sucesso
            showSuccess('Notas salvas com sucesso!')

        } catch (err) {
            console.error('Erro ao salvar notas:', err)
            showError('Erro ao salvar notas da aula. Tente novamente.')

            // Reverter a mudança local em caso de erro
            atualizarEstadoLocal(estadoAnterior)
        } finally {
            // Remover loading
            setLoadingNotas(prev => {
                const newSet = new Set(prev)
                newSet.delete(aulaId)
                return newSet
            })
        }
    }

    const toggleFavorita = async (aulaId: string) => {
        if (!user) return

        // Marcar como loading
        setLoadingFavoritas(prev => new Set(prev).add(aulaId))

        // Atualização otimista - atualizar o estado local imediatamente
        const atualizarEstadoLocal = () => {
            setModulos(prevModulos =>
                prevModulos.map(modulo => ({
                    ...modulo,
                    aulas: modulo.aulas.map(aula =>
                        aula.id === aulaId
                            ? { ...aula, favorita: !aula.favorita }
                            : aula
                    )
                }))
            )
        }

        // Atualizar estado local primeiro
        atualizarEstadoLocal()

        try {
            // Verificar se já existe um registro de progresso para esta aula
            const { data: existingProgress, error: checkError } = await supabase
                .from('progresso_aulas')
                .select('*')
                .eq('user_id', user.id)
                .eq('aula_id', aulaId)
                .single()

            if (checkError && checkError.code !== 'PGRST116') {
                throw checkError
            }

            if (existingProgress) {
                // Atualizar favorita no registro existente
                const { error: updateError } = await supabase
                    .from('progresso_aulas')
                    .update({
                        favorita: !existingProgress.favorita,
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', user.id)
                    .eq('aula_id', aulaId)

                if (updateError) throw updateError
            } else {
                // Criar novo registro com favorita = true (já que está sendo adicionada aos favoritos)
                const { error: insertError } = await supabase
                    .from('progresso_aulas')
                    .insert({
                        user_id: user.id,
                        aula_id: aulaId,
                        assistida: false,
                        data_assistida: null,
                        notas: '',
                        favorita: true,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })

                if (insertError) throw insertError
            }

        } catch (err) {
            console.error('Erro ao alterar favorito:', err)
            showError('Erro ao alterar status de favorito. Tente novamente.')

            // Reverter a mudança local em caso de erro
            atualizarEstadoLocal()
        } finally {
            // Remover loading
            setLoadingFavoritas(prev => {
                const newSet = new Set(prev)
                newSet.delete(aulaId)
                return newSet
            })
        }
    }

    const abrirNotasModal = (aula: Aula) => {
        setCurrentAulaNotas({
            id: aula.id,
            titulo: aula.titulo,
            notas: aula.notas || ''
        })
        setShowNotasModal(true)
    }

    const fecharNotasModal = () => {
        setShowNotasModal(false)
        setCurrentAulaNotas(null)
    }

    const toggleModulo = (moduloId: string) => {
        // Se o módulo já está aberto, fechar ele
        if (modulosExpandidos.has(moduloId)) {
            setModulosExpandidos(new Set())
        } else {
            // Fechar todos os outros e abrir apenas este
            setModulosExpandidos(new Set([moduloId]))
        }
    }

    const formatarDuracao = (minutos: number | null) => {
        if (!minutos) return 'N/A'
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const getNivelColor = (nivel: string) => {
        switch (nivel) {
            case 'iniciante':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'intermediario':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            case 'avancado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const calcularProgressoTotal = () => {
        const totalAulas = modulos.reduce((acc, modulo) => acc + modulo.total_aulas, 0)
        const aulasAssistidas = modulos.reduce((acc, modulo) => acc + modulo.aulas_assistidas, 0)
        return totalAulas > 0 ? Math.round((aulasAssistidas / totalAulas) * 100) : 0
    }

    // Função para recalcular progresso dos módulos após mudança de status
    const recalcularProgressoModulos = (modulosAtualizados: Modulo[]) => {
        return modulosAtualizados.map(modulo => {
            const aulasAssistidas = modulo.aulas.filter(aula => aula.assistida).length
            const progresso = modulo.total_aulas > 0 ? Math.round((aulasAssistidas / modulo.total_aulas) * 100) : 0

            return {
                ...modulo,
                aulas_assistidas: aulasAssistidas,
                progresso: progresso
            }
        })
    }

    // Função para obter o título do módulo atual
    const getCurrentModuleTitle = () => {
        if (!currentVideo?.aulaId) return 'Player de Vídeo'

        // Encontrar o módulo que contém a aula atual
        const currentModule = modulos.find(modulo =>
            modulo.aulas.some(aula => aula.id === currentVideo.aulaId)
        )

        return currentModule ? currentModule.titulo : 'Player de Vídeo'
    }

    // Função para filtrar aulas
    const filtrarAulas = (aulas: Aula[]) => {
        return aulas.filter(aula => {
            const matchStatus = filtros.status === 'todas' ||
                (filtros.status === 'assistidas' && aula.assistida) ||
                (filtros.status === 'pendentes' && !aula.assistida) ||
                (filtros.status === 'favoritas' && aula.favorita)

            const matchBusca = !filtros.busca ||
                aula.titulo.toLowerCase().includes(filtros.busca.toLowerCase()) ||
                (aula.descricao && aula.descricao.toLowerCase().includes(filtros.busca.toLowerCase()))

            return matchStatus && matchBusca
        })
    }

    // Função para obter próxima aula (para uso futuro)
    // const getProximaAula = (aulaAtual: Aula) => {
    //     const todasAulas = modulos.flatMap(m => m.aulas).sort((a, b) => {
    //         const moduloA = modulos.find(m => m.id === a.modulo_id)?.ordem_exibicao || 0
    //         const moduloB = modulos.find(m => m.id === b.modulo_id)?.ordem_exibicao || 0

    //         if (moduloA !== moduloB) return moduloA - moduloB
    //         return a.ordem_exibicao - b.ordem_exibicao
    //     })

    //     const indexAtual = todasAulas.findIndex(a => a.id === aulaAtual.id)
    //     return indexAtual < todasAulas.length - 1 ? todasAulas[indexAtual + 1] : null
    // }

    // Função para obter aula anterior (para uso futuro)
    // const getAulaAnterior = (aulaAtual: Aula) => {
    //     const todasAulas = modulos.flatMap(m => m.aulas).sort((a, b) => {
    //         const moduloA = modulos.find(m => m.id === a.modulo_id)?.ordem_exibicao || 0
    //         const moduloB = modulos.find(m => m.id === b.modulo_id)?.ordem_exibicao || 0

    //         if (moduloA !== moduloB) return moduloA - moduloB
    //         return a.ordem_exibicao - b.ordem_exibicao
    //     })

    //     const indexAtual = todasAulas.findIndex(a => a.id === aulaAtual.id)
    //     return indexAtual > 0 ? todasAulas[indexAtual - 1] : null
    // }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (error || !curso) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Curso não encontrado
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                            {error || 'O curso solicitado não existe ou você não tem acesso a ele.'}
                        </p>
                        <Button
                            onClick={() => navigate('/cursos')}
                            variant="btn_secondary"
                            className="px-8 py-3"
                        >
                            Voltar aos Cursos
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    const progressoTotal = calcularProgressoTotal()

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="w-full px-3 sm:px-4 lg:px-6 py-6">
                {/* Header Simplificado */}
                <div className="mb-6">
                    <Button
                        onClick={() => navigate('/cursos')}
                        variant="btn_secondary"
                        className="mb-4 flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Voltar aos Cursos
                    </Button>

                    {/* Título do Curso */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                    {curso.titulo}
                                </h1>
                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4" />
                                        <span>{formatarDuracao(curso.duracao_estimada)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Play className="h-4 w-4" />
                                        <span>{modulos.reduce((acc, m) => acc + m.total_aulas, 0)} aulas</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <BookOpen className="h-4 w-4" />
                                        <span>{modulos.length} módulos</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getNivelColor(curso.nivel)}`}>
                                        {curso.nivel}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Progresso: {progressoTotal}%
                                </div>
                                <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${progressoTotal}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Layout de Duas Colunas */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
                    {/* Player de Vídeo - Lado Esquerdo (Maior) */}
                    <div className="lg:col-span-3 lg:order-1">
                        <div className="lg:sticky lg:top-8">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {/* Header do Player */}
                                <div className="p-4 lg:p-5 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                        {getCurrentModuleTitle()}
                                    </h3>
                                    {currentVideo && (
                                        <p className="text-base text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                                            {currentVideo.title}
                                        </p>
                                    )}
                                </div>

                                {/* Player Container */}
                                <div className="p-4 lg:p-5">
                                    {currentVideo ? (
                                        <div className="space-y-6">
                                            {/* Video Player */}
                                            <div className="relative bg-black rounded-lg overflow-hidden">
                                                {(() => {
                                                    // Verificar se é vídeo do YouTube
                                                    const isYouTube = currentVideo.url.includes('youtube.com') || currentVideo.url.includes('youtu.be')
                                                    const getYouTubeVideoId = (url: string): string | null => {
                                                        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
                                                        const match = url.match(regExp)
                                                        return match && match[2].length === 11 ? match[2] : null
                                                    }

                                                    if (isYouTube) {
                                                        const youtubeVideoId = getYouTubeVideoId(currentVideo.url)
                                                        return youtubeVideoId ? (
                                                            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                                                <iframe
                                                                    src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0&modestbranding=1`}
                                                                    title={currentVideo.title}
                                                                    className="absolute top-0 left-0 w-full h-full"
                                                                    frameBorder="0"
                                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                    allowFullScreen
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center h-48 text-white">
                                                                <p>URL do YouTube inválida</p>
                                                            </div>
                                                        )
                                                    } else {
                                                        return (
                                                            <video
                                                                src={currentVideo.url}
                                                                className="w-full h-auto"
                                                                controls
                                                                preload="metadata"
                                                            />
                                                        )
                                                    }
                                                })()}
                                            </div>

                                            {/* Seção de Materiais da Aula */}
                                            {currentVideo.aulaId && (
                                                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                                                    {(() => {
                                                        console.log('📋 CursoDetalhes: Renderizando MaterialList com aulaId:', currentVideo.aulaId)
                                                        return (
                                                            <MaterialList
                                                                aulaId={currentVideo.aulaId}
                                                                showUploadButton={false}
                                                                showDeleteButton={false}
                                                                showAddButton={false}
                                                            />
                                                        )
                                                    })()}
                                                </div>
                                            )}

                                        </div>
                                    ) : (
                                        <div className="text-center py-12 lg:py-16">
                                            <Play className="mx-auto h-12 w-12 lg:h-16 lg:w-16 text-gray-400 dark:text-gray-500 mb-4 lg:mb-6" />
                                            <h4 className="text-lg lg:text-xl font-medium text-gray-900 dark:text-gray-100 mb-3">
                                                Selecione uma aula
                                            </h4>
                                            <p className="text-sm lg:text-base text-gray-500 dark:text-gray-400">
                                                Clique em "Assistir" em qualquer aula para reproduzir o vídeo aqui
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Coluna Direita - Módulos e Aulas (Menor) */}
                    <div className="lg:col-span-2 lg:order-2 space-y-4 lg:space-y-5">
                        {/* Filtros e Busca */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 lg:p-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                {/* Busca */}
                                <div className="flex-1">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Buscar aulas..."
                                            value={filtros.busca}
                                            onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Filtros */}
                                <div className="flex gap-2">
                                    <Button
                                        variant="btn_secondary"
                                        onClick={() => setShowFiltros(!showFiltros)}
                                        className="flex items-center gap-2"
                                    >
                                        <Filter className="h-4 w-4" />
                                        Filtros
                                        {showFiltros ? <ChevronUp className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>

                            {/* Opções de Filtro */}
                            {showFiltros && (
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { key: 'todas', label: 'Todas' },
                                            { key: 'pendentes', label: 'Pendentes' },
                                            { key: 'assistidas', label: 'Assistidas' },
                                            { key: 'favoritas', label: 'Favoritas' }
                                        ].map((opcao) => (
                                            <button
                                                key={opcao.key}
                                                onClick={() => setFiltros(prev => ({ ...prev, status: opcao.key as any }))}
                                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${filtros.status === opcao.key
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {opcao.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Módulos */}
                        <div className="space-y-4">
                            {modulos.map((modulo) => (
                                <div
                                    key={modulo.id}
                                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                                >
                                    {/* Header do Módulo */}
                                    <div
                                        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                        onClick={() => toggleModulo(modulo.id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="transition-transform duration-200 ease-in-out">
                                                    {modulosExpandidos.has(modulo.id) ? (
                                                        <ChevronDown className="h-5 w-5 text-gray-400" />
                                                    ) : (
                                                        <ChevronRight className="h-5 w-5 text-gray-400" />
                                                    )}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                                        {modulo.titulo}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {modulo.aulas_assistidas}/{modulo.total_aulas} aulas
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                                    {modulo.progresso}%
                                                </span>
                                                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                                    <div
                                                        className="bg-green-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                                                        style={{ width: `${modulo.progresso}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Aulas do Módulo */}
                                    <div
                                        className={`border-t border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 ease-in-out ${modulosExpandidos.has(modulo.id)
                                            ? 'max-h-screen opacity-100'
                                            : 'max-h-0 opacity-0'
                                            }`}
                                    >
                                        <div className={`transform transition-all duration-300 ease-in-out ${modulosExpandidos.has(modulo.id)
                                            ? 'translate-y-0 scale-100'
                                            : 'translate-y-2 scale-95'
                                            }`}>
                                            {filtrarAulas(modulo.aulas).map((aula) => (
                                                <div
                                                    key={aula.id}
                                                    className={`p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${currentVideo?.aulaId === aula.id
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                                        }`}
                                                >
                                                    {/* Mobile Layout */}
                                                    <div className="flex flex-col gap-3 sm:hidden">
                                                        {/* Content */}
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0 mt-1">
                                                                {currentVideo?.aulaId === aula.id ? (
                                                                    <div className="h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                                        <Play className="h-3 w-3 text-white" />
                                                                    </div>
                                                                ) : aula.assistida ? (
                                                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                                                ) : (
                                                                    <Play className="h-5 w-5 text-gray-400" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <h4 className={`font-medium ${aula.assistida ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                                                                        {aula.titulo}
                                                                    </h4>
                                                                    {aula.notas && aula.notas.trim() !== '' && (
                                                                        <StickyNote className="h-3 w-3 text-yellow-500 fill-current" />
                                                                    )}
                                                                </div>
                                                                {aula.descricao && (
                                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                        {aula.descricao}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-4 mt-2">
                                                                    <span className="text-xs text-gray-400">
                                                                        {formatarDuracao(aula.duracao)}
                                                                    </span>
                                                                    {aula.assistida && aula.data_assistida && (
                                                                        <span className="text-xs text-green-600 dark:text-green-400">
                                                                            Assistida em {new Date(aula.data_assistida).toLocaleDateString('pt-BR')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Buttons */}
                                                        <div className="flex items-center gap-2 ml-8">
                                                            {aula.video_url && (
                                                                <Button
                                                                    variant="btn_secondary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        if (aula.video_url) {
                                                                            openVideoPlayer(aula.video_url, aula.titulo, aula.id)
                                                                        }
                                                                    }}
                                                                    className="text-xs flex-1"
                                                                >
                                                                    {aula.assistida ? 'Assistir Novamente' : 'Assistir'}
                                                                </Button>
                                                            )}
                                                            {!aula.assistida ? (
                                                                <Button
                                                                    variant="btn_add"
                                                                    size="sm"
                                                                    onClick={() => marcarAulaAssistida(aula.id)}
                                                                    disabled={loadingAssistir.has(aula.id)}
                                                                    className={`text-xs flex-1 ${loadingAssistir.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    {loadingAssistir.has(aula.id) ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                            <span>Marcando...</span>
                                                                        </div>
                                                                    ) : (
                                                                        'Marcar como Assistida'
                                                                    )}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="btn_secondary"
                                                                    size="sm"
                                                                    onClick={() => removerAulaAssistida(aula.id)}
                                                                    disabled={loadingAssistir.has(aula.id)}
                                                                    className={`text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 flex-1 ${loadingAssistir.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    {loadingAssistir.has(aula.id) ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                            <span>Removendo...</span>
                                                                        </div>
                                                                    ) : (
                                                                        'Reassistir'
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="flex items-center gap-1 ml-8 mt-2">
                                                            <button
                                                                onClick={() => abrirNotasModal(aula)}
                                                                disabled={loadingNotas.has(aula.id)}
                                                                className={`p-1 transition-colors ${aula.notas && aula.notas.trim() !== ''
                                                                    ? 'text-yellow-500'
                                                                    : 'text-gray-400 hover:text-yellow-500'
                                                                    } ${loadingNotas.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                title={aula.notas && aula.notas.trim() !== '' ? 'Editar notas' : 'Adicionar notas'}
                                                            >
                                                                {loadingNotas.has(aula.id) ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <StickyNote className={`h-4 w-4 ${aula.notas && aula.notas.trim() !== '' ? 'fill-current' : ''}`} />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => toggleFavorita(aula.id)}
                                                                disabled={loadingFavoritas.has(aula.id)}
                                                                className={`p-1 transition-colors ${aula.favorita
                                                                    ? 'text-yellow-500'
                                                                    : 'text-gray-400 hover:text-yellow-500'
                                                                    } ${loadingFavoritas.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                title={aula.favorita ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                                                            >
                                                                {loadingFavoritas.has(aula.id) ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Star className={`h-4 w-4 ${aula.favorita ? 'fill-current' : ''}`} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Desktop Layout */}
                                                    <div className="hidden sm:flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex-shrink-0">
                                                                {currentVideo?.aulaId === aula.id ? (
                                                                    <div className="h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                                        <Play className="h-3 w-3 text-white" />
                                                                    </div>
                                                                ) : aula.assistida ? (
                                                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                                                ) : (
                                                                    <Play className="h-5 w-5 text-gray-400" />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <h4 className={`font-medium ${aula.assistida ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                                                                        {aula.titulo}
                                                                    </h4>
                                                                    {aula.notas && aula.notas.trim() !== '' && (
                                                                        <StickyNote className="h-3 w-3 text-yellow-500 fill-current" />
                                                                    )}
                                                                </div>
                                                                {aula.descricao && (
                                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {aula.descricao}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-4 mt-1">
                                                                    <span className="text-xs text-gray-400">
                                                                        {formatarDuracao(aula.duracao)}
                                                                    </span>
                                                                    {aula.assistida && aula.data_assistida && (
                                                                        <span className="text-xs text-green-600 dark:text-green-400">
                                                                            Assistida em {new Date(aula.data_assistida).toLocaleDateString('pt-BR')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {aula.video_url && (
                                                                <Button
                                                                    variant="btn_secondary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        if (aula.video_url) {
                                                                            openVideoPlayer(aula.video_url, aula.titulo, aula.id)
                                                                        }
                                                                    }}
                                                                    className="text-xs"
                                                                >
                                                                    {aula.assistida ? 'Assistir Novamente' : 'Assistir'}
                                                                </Button>
                                                            )}
                                                            {!aula.assistida ? (
                                                                <Button
                                                                    variant="btn_add"
                                                                    size="sm"
                                                                    onClick={() => marcarAulaAssistida(aula.id)}
                                                                    disabled={loadingAssistir.has(aula.id)}
                                                                    className={`text-xs ${loadingAssistir.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    {loadingAssistir.has(aula.id) ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                            <span>Marcando...</span>
                                                                        </div>
                                                                    ) : (
                                                                        'Marcar como Assistida'
                                                                    )}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="btn_secondary"
                                                                    size="sm"
                                                                    onClick={() => removerAulaAssistida(aula.id)}
                                                                    disabled={loadingAssistir.has(aula.id)}
                                                                    className={`text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 ${loadingAssistir.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                >
                                                                    {loadingAssistir.has(aula.id) ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                            <span>Removendo...</span>
                                                                        </div>
                                                                    ) : (
                                                                        'Reassistir'
                                                                    )}
                                                                </Button>
                                                            )}

                                                            {/* Action Buttons */}
                                                            <button
                                                                onClick={() => abrirNotasModal(aula)}
                                                                disabled={loadingNotas.has(aula.id)}
                                                                className={`p-1 transition-colors ${aula.notas && aula.notas.trim() !== ''
                                                                    ? 'text-yellow-500'
                                                                    : 'text-gray-400 hover:text-yellow-500'
                                                                    } ${loadingNotas.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                title={aula.notas && aula.notas.trim() !== '' ? 'Editar notas' : 'Adicionar notas'}
                                                            >
                                                                {loadingNotas.has(aula.id) ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <StickyNote className={`h-4 w-4 ${aula.notas && aula.notas.trim() !== '' ? 'fill-current' : ''}`} />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => toggleFavorita(aula.id)}
                                                                disabled={loadingFavoritas.has(aula.id)}
                                                                className={`p-1 transition-colors ${aula.favorita
                                                                    ? 'text-yellow-500'
                                                                    : 'text-gray-400 hover:text-yellow-500'
                                                                    } ${loadingFavoritas.has(aula.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                title={aula.favorita ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                                                            >
                                                                {loadingFavoritas.has(aula.id) ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Star className={`h-4 w-4 ${aula.favorita ? 'fill-current' : ''}`} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Modal de Parabéns */}
                <ModalParabens
                    isOpen={showModalParabens}
                    onClose={() => setShowModalParabens(false)}
                    cursoTitulo={curso?.titulo || ''}
                    progresso={calcularProgressoTotal()}
                />


                {/* Modal de Notas */}
                {showNotasModal && currentAulaNotas && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    Notas da Aula
                                </h3>
                                <button
                                    onClick={fecharNotasModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {currentAulaNotas.titulo}
                                </h4>
                                <textarea
                                    value={currentAulaNotas.notas}
                                    onChange={(e) => setCurrentAulaNotas(prev =>
                                        prev ? { ...prev, notas: e.target.value } : null
                                    )}
                                    placeholder="Adicione suas notas sobre esta aula..."
                                    className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="btn_secondary"
                                    onClick={fecharNotasModal}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="btn_add"
                                    onClick={() => {
                                        if (currentAulaNotas) {
                                            salvarNotasAula(currentAulaNotas.id, currentAulaNotas.notas)
                                            fecharNotasModal()
                                        }
                                    }}
                                >
                                    Salvar Notas
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de Upload de Materiais */}
            {showMaterialUpload && currentVideo?.aulaId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                    Adicionar Material à Aula
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setShowMaterialUpload(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <MaterialUploadAlternativo
                                aulaId={currentVideo.aulaId}
                                onUploadSuccess={handleMaterialUploadSuccess}
                                onUploadError={handleMaterialUploadError}
                                className="mb-6"
                            />

                            <div className="flex justify-end">
                                <Button
                                    variant="btn_secondary"
                                    onClick={() => setShowMaterialUpload(false)}
                                >
                                    Fechar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    )
}
